import type { Game } from "@e308/core";
import { toConflictEvent } from "./conflict-event.js";
import type { ConflictContent } from "./definition.js";
import { observeDiplomacy } from "./diplomacy-state.js";
import type { DiplomacyState } from "./diplomacy-types.js";
import type { Micro } from "./exact-numbers.js";
import type { MatchLifecycleState } from "./lifecycle-types.js";
import type { ConflictEvent, SeatObservation } from "./types.js";

export function observeMatch(
  game: Game<Micro>,
  content: ConflictContent,
  seatId: string,
): SeatObservation {
  const snapshot = game.getSnapshot();
  const self = content.observe(snapshot, seatId);
  return Object.freeze({
    revision: snapshot.revision,
    gameTimeMs: snapshot.gameTimeMs,
    self,
    seats: Object.freeze(content.seats.map((seat) => content.observe(snapshot, seat.seat.id))),
    match: snapshot.records?.[content.lifecycle.id]?.value as unknown as MatchLifecycleState,
    diplomacy: observeDiplomacy(
      snapshot.records?.[content.diplomacy.id]?.value as unknown as DiplomacyState,
      seatId,
    ),
  });
}

export function readMatchEvents(
  game: Game<Micro>,
  afterSequence: bigint,
  seatId?: string,
): readonly ConflictEvent[] {
  const result = game.readDomainEvents(afterSequence);
  /* v8 ignore next -- core journal gap behavior is certified by the K03 engine suite */
  if (result.kind === "gap") throw new RangeError("Event cursor predates retained history");
  return Object.freeze(
    result.events
      .filter(
        (event) =>
          seatId === undefined ||
          event.audience.kind === "public" ||
          (event.audience.kind === "seats" && event.audience.seatIds.includes(seatId)),
      )
      .map(toConflictEvent),
  );
}
